import moment from 'moment';
import cookies from 'js-cookie';
import { delimiters } from '../shared/constants';
import { select } from '../shared/utils/dom';
import { createAccount, showSuccessErrorMessage, getErrorMessages, getSuccessErrorSecondaryMessage, removeErrors } from './api';
import { createError, removeError } from '../shared/formErrors';
import { regex, isPasswordValid } from '../shared/constants/validation';
import multistepMixin from '../shared/vue/mixins/multistep';
import togglePassword from '../shared/vue/mixins/togglePassword';
import accountLookup from '../shared/vue/mixins/accountLookup';
import scrollUp from '../shared/vue/mixins/scrollUp';
import dataLayerPush from '../shared/utils/analytics';

// re-assign some API methods
const { verifyAccountOwnership } = createAccount;
const { getEbillEnrollment } = createAccount;
const createAccountEmailValidationCountCookieName = 'createAccountEmailValidationCount';

/**
 * Data for the Vue application
 */
// set up data vales
const data = function () {
  return {
    // for accessibility in live regions
    liveText: '',
    // search button for account number field should default to disabled
    disabledAccountNumber: true,
    // The service addresses (will be populated via API response)
    modelValidationResults: [],
    // secondary lookup field ID
    secondaryLookupFieldName: null,
    // the first step submit button disabled by default
    enableConfirmAccountButton: false,
    // first name
    modelFirstName: null,
    // last name
    modelLastName: null,
    // business name
    modelBusinessName: null,
    // email
    modelEmail: null,
    modelConfirmEmail: null,
    // password
    modelPassword: null,
    // confirm password
    modelConfirmPassword: null,
    // password valid
    modelPasswordValid: false,
    // confirm password matches
    modelConfirmPasswordMatch: false,
    // set to true if no accounts found
    noAccountsFound: false,
    // list of customer account IDs the user has selected
    selectedCustomerAccounts: [],
    // check if 'Select All' checkbox is checked
    allAccountsSelected: false,
    termsAgree: false,
    termsModalIsOpen: false,
    isReady: false,
    errorMessages: getErrorMessages(), // JSON.parse(document.getElementById('errors').innerHTML),
    isEnrolledInPaperless: false,
    paperlessAgree: true,
    isEligibleForPaperless: false,
    accountIsActive: false, // initially set to false to hide Paperless Enrollment
    isPOAAccount: false,
    isErrorMessage: false,
    emailValidationFailed: false,
    emailValidationFailedCount: 0,
  };
};

// Watch
const watch = {
  /**
   * Changes live text depending on the form step
   */
  step(newVal, oldVal) {
    if (newVal === 2 && oldVal === 1) {
      this.liveText = 'Moving to Step 2 to confirm your account information.';
    } else if (newVal === 1 && oldVal === 2) {
      this.liveText = 'You are now back on Step 1 to look up your account.';
    }
  },
};

// Methods
const methods = {
  // execute the recaptcha widget on click
  executeRecaptcha() {
    this.$refs.recaptcha.execute();
  },
  /**

   * Click on the confirm account button
   */
  clickConfirmAccount() {
    let hasError = false;
    const accountNumberInput = select('#account-number-input');
    const phoneNumberInput = select('#phone-input');

    // double check there's either an account number or phone number provided
    if (this.unmaskedAccountNumber || this.unmaskedPhoneNumber) {
      removeError(accountNumberInput, this.$refs.accountNumberError);
      removeError(phoneNumberInput, this.$refs.phoneNumberError);
    } else {
      createError(accountNumberInput, this.$refs.accountNumberError, 'Please provide an account number.');
      createError(phoneNumberInput, this.$refs.phoneNumberError, 'Please provide a phone number.');
      hasError = true;
    }

    if (hasError) {
      this.liveText = 'The form has errors. Please make sure your information is correct.';
    } else {
      this.loading = true;

      if (window.fabricator) {
        setTimeout(() => {
          this.modelValidationResults = [
            {
              customerAccountId: '123456789',
              addresses: [
                {
                  address1: '274 Marconi Blvd',
                  address2: 'Suite 300',
                  city: 'Columbus',
                  state: 'OH',
                  zip: 43215,
                },
                {
                  address1: '1234 State St',
                  address2: '',
                  city: 'Columbus',
                  state: 'OH',
                  zip: 43215,
                },
              ],
              valid: true,
            },
            // {
            //   customerAccountId: '123456780',
            //   addresses: [
            //     {
            //       address1: '200 S Civic Center Drive',
            //       address2: '',
            //       city: 'Columbus',
            //       state: 'OH',
            //       zip: 43215,
            //     },
            //   ],
            //   valid: true,
            // },
          ];
          this.loading = false;
          this.nextStep();
        }, 500);
      } else {
        // prepare the API info
        const params = {
          // use date of birth because we have a value
          accountId: this.unmaskedAccountNumber,
          phone: this.unmaskedPhoneNumber,
          secondarySearchValues: [],
        }

        // populate the verify criteria
        // TODO: remove (no longer used)
        if (this.modelDateOfBirth.length > 0) {
          this.modelDateOfBirth.forEach(dob => {
            if (dob) {
              params.secondarySearchValues.push({
                secondaryValue: dob.replace(/ /g, ""),
                secondarySearchValueType: "DATE_OF_BIRTH",
              });
            }
          });
        }
        if (this.modelLast4Ssn.length > 0) {
          this.modelLast4Ssn.forEach(ssn => {
            if (ssn) {
              params.secondarySearchValues.push({
                secondaryValue: ssn,
                secondarySearchValueType: "LAST_4_SOCIAL",
              });
            }
          });
        }
        if (this.modelTaxId.length > 0) {
          this.modelTaxId.forEach(tid => {
            if (tid) {
              params.secondarySearchValues.push({
                secondaryValue: tid.replace(/-/g, ""),
                secondarySearchValueType: "TAX_ID",
              });
            }
          });
        }// TODO: remove (no longer used)
        if (this.modelPin.length > 0) {
          this.modelPin.forEach(pin => {
            if (pin) {
              params.secondarySearchValues.push({
                // WEBPAY-1921
                secondaryValue: this.modelLdc === 'BS' || this.modelLdc === 'NI' ? pin : pin.toUpperCase(),
                secondarySearchValueType: "PIN",
              });
            }
          });
        }

        if (this.modelLdc) {
          params.ldc = this.modelLdc.toUpperCase();
        }

        // do the api lookup
        verifyAccountOwnership(params)
          .then((response) => {
            if (response.data && response.data.entries) {
              const entries = response.data.entries;
              if (entries && entries.length) {
                this.loading = false;
                this.modelValidationResults = entries;
                // WEBPAY-638: if there is exactly one account, select it by default
                if (this.maxValidatedValidationResults.length === 1) {
                  this.selectAllAddressesByDefault();
                  if (params.accountId) {
                    getEbillEnrollment(params.accountId)
                      .then((detail) => {
                        this.isEnrolledInPaperless = detail.data.ebill === true || detail.data.ebillType === 'CheckFree';
                        this.accountIsActive = detail.data.active;
                        this.isPOAAccount = detail.data.propertyOwnerLandlordAgreementFlag;
                      })
                  }
                }

                // Update form progress bars for second step if there is at least one valid account
                const numValidated = response.data.validatedCount;
                if (numValidated >= 1) {
                  if (this.modelValidationResults.filter(item => item.addresses.length >= 1).length === 0) {
                    // show error if account doesn't have any valid addresses
                    showSuccessErrorMessage(this.errorMessages, "RegisterAccount-CustomerAccountNoAddress");
                    this.scrollUp();
                  } else {
                    this.nextStep();
                  }
                } else {
                  showSuccessErrorMessage(this.errorMessages, "RegisterAccount-VerifyAccountInvalid");
                  this.scrollUp();
                }
              }
            }
          })
          .catch(() => {
            this.loading = false;
          });
      }
    }
  },

  selectAllAddressesByDefault() {
    this.selectedCustomerAccounts = this.maxValidatedValidationResults.map(e => e.customerAccountId);
    this.allAccountsSelected = true;
  },

  updateSelectAllAccounts() {
    if (this.selectedCustomerAccounts.length === this.modelValidationResults.length) {
      this.allAccountsSelected = true;
    } else {
      this.allAccountsSelected = false;
    }
  },

  selectAllAccounts() {
    this.allAccountsSelected = !this.allAccountsSelected;
    this.selectedCustomerAccounts = [];

    if (this.allAccountsSelected) {
      this.selectedCustomerAccounts = this.maxValidatedValidationResults.map(e => e.customerAccountId);
    }
  },

  /**
   * Click on "Wrong address, double check account information"
   */
  clickDoubleCheck() {
    this.modelFirstName = null;
    this.modelLastName = null;
    this.modelBusinessName = null;
    this.modelEmail = null;
    this.modelConfirmEmail = null;
    this.modelPassword = null;
    this.modelConfirmPassword = null;
    this.modelPasswordValid = false;
    this.modelConfirmPasswordMatch = false;
    this.selectedCustomerAccounts = [];
    this.allAccountsSelected = false;
    this.enableConfirmAccountButton = false;
    removeError(this.$refs.firstName, this.$refs.firstNameError);
    removeError(this.$refs.lastName, this.$refs.lastNameError);
    removeError(this.$refs.email, this.$refs.emailError);
    removeError(this.$refs.password, this.$refs.passwordError);
    removeError(this.$refs.confirmPassword, this.$refs.confirmPasswordError);

    // clear lookup and verification fields when going back to the previous step
    if (this.modelAccountNumber) {
      this.modelAccountNumber = '';
    } else {
      this.modelPhone = '';
    }
    this.clearVerifyFields();
    // update form progress bars
    this.previousStep();
  },

  /**
   * Handle the JSON response of the lookup for getting secondary lookup field
   * @this Vue The Vue app instance
   * @param {object} response
   * @param {string} response.hasDateOfBirth
   * @param {string} response.hasTaxId
   * @param {string} response.hasLastFourSocial
   */
  handleGetSecondaryLookupFieldResponse(response) {
    if (response.dateOfBirthCount > 0 || response.lastFourSocialCount > 0 || response.taxIdCount > 0 || response.pinCount > 0) {
      this.modelDateOfBirth = Array(response.dateOfBirthCount || 0).map(() => '');
      this.modelLast4Ssn = Array(response.lastFourSocialCount || 0).map(() => '');
      this.modelTaxId = Array(response.taxIdCount || 0).map(() => '');
      this.modelPin = Array(response.pinCount || 0).map(() => '');
      this.showVerifyFields = true;
      this.disabledAccountNumber = true;
      Vue.nextTick(() => {
        document.querySelector('.create-account__verify input').focus();
      });
    } else {
      showSuccessErrorMessage(this.errorMessages, "RegisterAccount-NoSecondaryInfoAvailable");
    }
  },

  /**
   * Keyup on password field
   */
  keyUpPassword(event) {
    // password validity check
    if (event.target === this.$refs.password) {
      if (this.modelPassword && this.modelPassword.length < 8) {
        this.modelPasswordValid = false;
      } else if (!isPasswordValid(this.$refs.password.value)) {
        this.modelPasswordValid = false;
      } else {
        this.modelPasswordValid = true;
      }
    }

    // confirm password validity check
    if (event.target === this.$refs.confirmPassword) {
      // check they are the same thing
      if (this.modelConfirmPassword && this.modelPassword && this.modelPasswordValid && this.modelConfirmPassword === this.modelPassword) {
        this.modelConfirmPasswordMatch = true;
        removeError(this.$refs.password, this.$refs.passwordError);
      } else {
        this.modelConfirmPasswordMatch = false;
        removeError(this.$refs.confirmPassword, this.$refs.confirmPasswordError);
      }
    }
  },

  /**
   * Keyup on any Verify field
   */
  keyupOnVerifyField() {
    let dobPass = true;
    let ssnPass = true;
    let taxPass = true;
    let pinPass = true;
    if (this.modelDateOfBirth.length > 0) {
      dobPass = this.modelDateOfBirth.every(dob => regex.dateOfBirth.test(dob));
    }
    if (this.modelTaxId.length > 0) {
      taxPass = this.modelTaxId.every(tid => tid !== '');
    }
    if (this.modelLast4Ssn) {
      ssnPass = this.modelLast4Ssn.every(ssn => ssn !== '' && ssn.length === 4);
    }
    if (this.modelPin) {
      pinPass = this.modelPin.every(pin => pin !== '');
    }

    this.enableConfirmAccountButton = dobPass && ssnPass && taxPass && pinPass;
  },

  /**
   * Form submit handler
   */
  submitForm(event) {
    event.preventDefault();
    let hasError = false;

    // validate at least one account selected
    if (!this.selectedCustomerAccounts || this.selectedCustomerAccounts.length === 0) {
      createError(this.$refs.selectedAccountInput[0], this.$refs.selectedAccountError[0], 'Please select at least one account to register.');
      hasError = true;
    } else {
      removeError(this.$refs.selectedAccountInput[0], this.$refs.selectedAccountError[0]);
    }
    // validate first name
    if (!this.modelFirstName) {
      createError(this.$refs.firstName, this.$refs.firstNameError, 'Please provide a first name.');
      hasError = true;
    } else {
      removeError(this.$refs.firstName, this.$refs.firstNameError);
    }
    // validate last name
    if (!this.modelLastName) {
      createError(this.$refs.lastName, this.$refs.lastNameError, 'Please provide a last name.');
      hasError = true;
    } else {
      removeError(this.$refs.lastName, this.$refs.lastNameError);
    }
    // validate email
    if (!this.modelEmail) {
      createError(this.$refs.email, this.$refs.emailError, 'Please provide an email address.');
      hasError = true;
    } else if (!regex.email.test(this.modelEmail)) {
      createError(this.$refs.email, this.$refs.emailError, getSuccessErrorSecondaryMessage(this.errorMessages, this.$refs.email.dataset.key),
      );
      hasError = true;
    } else {
      removeError(this.$refs.email, this.$refs.emailError);
    }
    // validate confirm email
    if (this.modelEmail.toLowerCase() !== this.modelConfirmEmail.toLowerCase()) {
      createError(this.$refs.confirmEmail, this.$refs.confirmEmailError, getSuccessErrorSecondaryMessage(this.errorMessages, this.$refs.confirmEmail.dataset.key));
      hasError = true;
    } else {
      removeError(this.$refs.confirmEmail, this.$refs.confirmEmailError);
    }

    // validate password fields
    if (!this.modelPassword) {
      createError(this.$refs.password, this.$refs.passwordError, 'Please provide a password.');
      hasError = true;
    } else if (!isPasswordValid(this.$refs.password.value)) {
      createError(this.$refs.password, this.$refs.passwordError, getSuccessErrorSecondaryMessage(this.errorMessages, this.$refs.password.dataset.key));
      hasError = true;
    } else {
      removeError(this.$refs.password, this.$refs.passwordError);
    }
    // validate confirm password field
    if (!this.modelConfirmPassword) {
      createError(this.$refs.confirmPassword, this.$refs.confirmPasswordError, 'Please confirm password.');
      hasError = true;
    } else if (this.modelPassword !== this.modelConfirmPassword) {
      createError(this.$refs.confirmPassword, this.$refs.confirmPasswordError, getSuccessErrorSecondaryMessage(this.errorMessages, this.$refs.confirmPassword.dataset.key));
      hasError = true;
    } else {
      removeError(this.$refs.confirmPassword, this.$refs.confirmPasswordError);
    }

    if (!hasError) {
      // if the passwords are shown, switch back to password inputs before submit
      this.hidePasswordText();
      const createRequest = this.buildCreateAccountRequest();
      this.loading = true;
      this.emailValidationFailed = false;
      createAccount.create(createRequest)
        .then(() => {
          dataLayerPush('CreateAccount');
          document.querySelector('#login-form').submit();
        })
        .catch((error) => {
          this.loading = false;
          if (error.response.status === 400 && error.response.data.error.code === 1021) {
            removeErrors();
            this.isErrorMessage = true;
          }
          if (error.response.status === 400 && error.response.data.error.code === 1060) {
            removeErrors();
            this.emailValidationFailed = true;
            this.emailValidationFailedCount = this.emailValidationFailedCount + 1;
            cookies.set(createAccountEmailValidationCountCookieName, this.emailValidationFailedCount);
          }
          this.scrollUp();
        });
    }
  },

  buildCreateAccountRequest() {
    return {
      email: this.modelEmail,
      password: this.modelPassword,
      firstName: this.modelFirstName,
      lastName: this.modelLastName,
      businessName: this.modelBusinessName,
      confirmPassword: this.modelConfirmPassword,
      ldc: this.modelLdc,
      phoneNumber: this.unmaskedPhoneNumber,
      dateOfBirth: this.formattedDatesOfBirth,
      last4Ssn: this.modelLast4Ssn.filter(ssn => ssn),
      taxId: this.modelTaxId.filter(tid => tid).map(tid => tid.replace(/-/g, "")),
      pins: this.modelPin.filter(pin => pin),
      registrations: this.buildRegistrationListFromSelectedAccounts(),
      enrollInPaperless: this.checkEnrollInPaperless(),
      emailValidationFailedCount: this.emailValidationFailedCount,
    };
  },

  buildRegistrationListFromSelectedAccounts() {
    return this.selectedCustomerAccounts.map(accountId => {
      const validationEntries = this.modelValidationResults.filter(v => v.customerAccountId === accountId);
      return {
        customerAccountId: accountId,
        ldc: validationEntries[0].ldc,
        nickname: validationEntries[0].addresses[0].address1,
      };
    });
  },

  checkEnrollInPaperless() {
    if (this.isPaperlessEligible()) {
      return this.paperlessAgree;
    }
    return false;
  },

  isPaperlessEligible() {
    if (this.modelValidationResults.length === 1
      && this.accountIsActive
      && !this.isEnrolledInPaperless
      && !this.isPOAAccount) {
      return true;
    }

    return false;
  },

  checkLength() {
    if (this.modelEmail.length > 50) {
      createError(this.$refs.email, this.$refs.emailError, 'Email address cannot be more than 50 characters.');
    } else {
      removeError(this.$refs.email, this.$refs.emailError);
    }
  },
};


// Computeds
const computed = {
  validatedValidationResults() {
    return this.modelValidationResults.filter(item => item.valid);
  },
  maxValidatedValidationResults() {
    // Per CXCDR-2084, we are now allowing unlimited registrations at once. This may need to change in the future. If it does,
    // this should return a slice of validatedValidationResults.
    return this.validatedValidationResults;
  },
  invalidValidationResults() {
    return this.modelValidationResults.filter(item => !item.valid);
  },
  tooManyAccountsWarning() {
    // Per CXCDR-2084, we are now allowing unlimited registrations at once. This may need to change in the future, so if it does,
    // the code below should be uncommented and the number updated.
    // const validatedAccountLen = this.validatedValidationResults.length;
    // if (validatedAccountLen > 10) {
    //   return `We have located ${validatedAccountLen} accounts. Only the first 10 can be registered online. You may add additional accounts later.`;
    // }
    return null;
  },
  validationResultsErrorMessage() {
    const invalidResults = this.modelValidationResults.filter(item => !item.valid);
    if (invalidResults.length === 0) {
      return null;
    }

    let message = getSuccessErrorSecondaryMessage(this.errorMessages, "RegisterAccount-SubsetAccountsFound");
    let accNo = ''
    invalidResults.forEach(result => {
      if (accNo === '') {
        accNo = `****${result.customerAccountId}`;
      } else {
        accNo = `${accNo}, ****${result.customerAccountId}`;
      }
    });
    message = message.replace("{[accounts]}", accNo);
    return message;
  },
  shouldDisableCreateAccount() {
    // Only enable create account button if there is at least one validated lookup result.
    return !this.termsAgree || this.validatedValidationResults.length === 0 || this.emailValidationFailedThresholdLimitReached;
  },
  formattedDatesOfBirth() {
    return this.modelDateOfBirth.filter(dob => dob).map(d => moment(d, 'MM/DD/YYYY').format("YYYY-MM-DD[T]HH:mm:ss[Z]"));
  },
  emailValidationFailedThresholdLimitReached() {
    return this.emailValidationFailedCount >= 3;
  },
}

// mounted
const mounted = function () {
  // when Vue app mounts,
  // check if there's already been data filled in for creating an account
  if (this.$el.dataset.initialEmail) {
    this.modelEmail = this.$el.dataset.initialEmail;
  }
  this.emailValidationFailedCount = cookies.get(createAccountEmailValidationCountCookieName) ? JSON.parse(cookies.get(createAccountEmailValidationCountCookieName)) : 0;
};

// created
const created = function () {
  Event.$on('UpdateNoAccountsFoundFlag', (response) => {
    if (response) {
      this.noAccountsFound = response.noAccountsFound;
      removeErrors();
    }
  });
}

export default function () {
  const el = select('[data-vue-app="create-account"]');

  // Init LDC from data attribute since it must be set by server
  if (el) {
    new Vue({ // eslint-disable-line no-new
      el,
      delimiters,
      data,
      created,
      methods,
      computed,
      mixins: [multistepMixin, scrollUp, accountLookup, togglePassword],
      mounted,
      watch,
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/createAccount.js